import { Tab, Tabs } from '@sortlist-frontend/design-system';
import { CircleHelp } from '@sortlist-frontend/design-system/icons';
import { useState } from 'react';

import { RichText } from '_features/RankingPage/components/RichText';

import type { GuideTab } from '../types';
import { GuideBlockList } from './GuideBlockList';

interface GuideTabsProps {
  label: string;
  variant: 'tabs' | 'nodes';
  tabs: readonly GuideTab[];
  locale: string;
  serviceName: string;
  path: string;
}

export const GuideTabs = (props: GuideTabsProps) => {
  const { label, variant, tabs, locale, serviceName, path } = props;
  const [selected, setSelected] = useState(tabs[0].id);

  return (
    <Tabs
      aria-label={label}
      variant="light"
      selectedKey={selected}
      onSelectionChange={(key) => setSelected(key.toString())}
      classNames={{
        tabList: variant === 'nodes' ? 'flex-wrap gap-2 p-0' : 'flex-wrap gap-2 p-0 md:flex-nowrap',
        tab: 'h-auto max-w-none flex-1 justify-start rounded-2xl border border-secondary-200 bg-white px-4 py-3 text-left data-[hover=true]:border-primary-300 data-[selected=true]:border-primary-500 data-[selected=true]:bg-primary-50',
        tabContent: 'flex w-full flex-col items-start text-secondary-700 group-data-[selected=true]:text-secondary-950',
        cursor: 'hidden',
        panel: 'mt-4 rounded-2xl border border-secondary-200 bg-white p-5',
      }}>
      {tabs.map((tab) => (
        <Tab
          key={tab.id}
          title={
            <span className="flex flex-col items-start">
              {tab.eyebrow != null ? (
                <span className="text-xs font-semibold uppercase tracking-wide text-primary-600">{tab.eyebrow}</span>
              ) : null}
              <span className="font-semibold">{tab.title}</span>
              {tab.subtitle != null ? <span className="text-xs text-secondary-500">{tab.subtitle}</span> : null}
            </span>
          }>
          {tab.heading != null ? <p className="mb-1 font-bold text-secondary-950">{tab.heading}</p> : null}
          {tab.cards != null ? (
            <div className="grid gap-3 sm:grid-cols-2">
              {tab.cards.map((card) => (
                <div key={card.title} className="rounded-xl bg-secondary-50 p-4">
                  <p className="font-semibold text-secondary-950">{card.title}</p>
                  <p className="mt-1 text-sm leading-relaxed text-secondary-700">
                    <RichText text={card.text} />
                  </p>
                </div>
              ))}
            </div>
          ) : null}
          {tab.blocks != null ? (
            <GuideBlockList blocks={tab.blocks} locale={locale} serviceName={serviceName} path={path} />
          ) : null}
          {tab.ask != null ? (
            <p className="mt-3 flex gap-2 text-sm font-semibold text-secondary-950">
              <CircleHelp size={18} className="mt-0.5 shrink-0 text-primary-500" aria-hidden="true" />
              <span>{tab.ask}</span>
            </p>
          ) : null}
        </Tab>
      ))}
    </Tabs>
  );
};
